import { Component, Input, SimpleChanges } from "@angular/core";
import * as Highcharts from "highcharts";
import MapModule from "highcharts/modules/map";
import { Nullable } from "primeng/ts-helpers";
import { StatusId } from "@core/models/auxiliary-company-models";
import { DataFilter } from "../dynamic-table/dynamic-table.component";
import { CompaniesService } from "src/app/services/companies/companies.service";
import { MessageService } from "primeng/api";
import { catchError, of, takeUntil } from "rxjs";
import { RouterService } from "src/app/services/router/router.service";
import { InvestorTab } from "@core/models/types";

export interface ChartData {
  companyId: number;
  name: string;
  statusId: number;
  lastInteractionDate: string;
  interactionScore: number;
  notesCount: number;
  emailCount: number;
  phoneCount: number;
  videoCallCount: number;
  meetingCount: number;
}


MapModule(Highcharts);
@Component({
  selector: "bpc-interaction-scatter-chart",
  templateUrl: "./interaction-scatter-chart.component.html",
  styleUrl: "./interaction-scatter-chart.component.scss",
})
export class InteractionScatterChartComponent {
  @Input() interactionFilter: DataFilter = {};

  mapLoading: boolean = true;
  companyData!: ChartData[];
  Highcharts: typeof Highcharts = Highcharts;
  popupChart!: Highcharts.Chart;
  options!: Highcharts.Options;
  chartConstructor = "chart"; // Specifies that it's a map
  visible: boolean = false;
  selectedCompany: Nullable<ChartData> = null;

  constructor(
    private companiesService: CompaniesService, 
    private messageService: MessageService,
    private routerService: RouterService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes["interactionFilter"]) {
      this.generateInteractionData();
    }
  }

  initializeChart() {
    if (!this.companyData) {
      return;
    }

    const getData = (state: number) => {
      const currentTime = new Date().getTime(); // Get the current timestamp

      return this.companyData
        .filter((elm) => elm.statusId === state && new Date(elm.lastInteractionDate).getTime() <= currentTime) // Filter out future dates
        .map((elm) => ({
          name: elm.name,
          x: new Date(elm.lastInteractionDate).getTime(), // Convert date to timestamp
          y: elm.interactionScore,
        }));
    };

    const isIpad = () => {
      const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera;
      return /iPad|Macintosh/i.test(userAgent) && navigator.maxTouchPoints && navigator.maxTouchPoints > 1;
    };

    const series: Highcharts.SeriesOptionsType[] = [
      {
        type: "scatter",
        name: "Undefined",
        id: "1",
        marker: {
          symbol: "circle",
        },
        data: getData(StatusId.None),
      },
      {
        type: "scatter",
        name: "Unlikely",
        id: "2",
        marker: {
          symbol: "circle",
        },
        data: getData(StatusId.Unlikely),
      },
      {
        type: "scatter",
        name: "Early",
        id: "1",
        marker: {
          symbol: "circle",
        },
        data: getData(StatusId.Early),
      },
      {
        type: "scatter",
        name: "Monitor",
        id: "2",
        marker: {
          symbol: "circle",
        },
        data: getData(StatusId.Monitor),
      },
      {
        type: "scatter",
        name: "Contender",
        id: "1",
        marker: {
          symbol: "circle",
        },
        data: getData(StatusId.Contender),
      },
      {
        type: "scatter",
        name: "Must Meet",
        id: "2",
        marker: {
          symbol: "circle",
        },
        data: getData(StatusId.MustMeet),
      },
      {
        type: "scatter",
        name: "Pearl",
        id: "1",
        marker: {
          symbol: "circle",
        },
        data: getData(StatusId.Pearl),
      },
      {
        type: "scatter",
        name: "Cant Crack",
        id: "2",
        marker: {
          symbol: "circle",
        },
        data: getData(StatusId.CantCrack),
      },
      {
        type: "scatter",
        name: "Firmwide Elevation",
        id: "2",
        marker: {
          symbol: "circle",
        },
        data: getData(StatusId.firmWideElevation),
      },
      {
        type: "scatter",
        name: "PipelineDeal",
        id: "2",
        marker: {
          symbol: "circle",
        },
        data: getData(StatusId.PipelineDeal),
      },
      {
        type: "scatter",
        name: "Priority Pipeline",
        id: "1",
        marker: {
          symbol: "circle",
        },
        data: getData(StatusId.PriorityPipeline),
      },
      {
        type: "scatter",
        name: "Active Deal",
        id: "1",
        marker: {
          symbol: "circle",
        },
        data: getData(StatusId.ActiveDeal),
      },
      {
        type: "scatter",
        name: "IC",
        id: "2",
        marker: {
          symbol: "circle",
        },
        data: getData(StatusId.IC),
      },
      {
        type: "scatter",
        name: "Hearts Minds",
        id: "1",
        marker: {
          symbol: "circle",
        },
        data: getData(StatusId.HeartsMinds),
      },
      {
        type: "scatter",
        name: "Portfolio M&A",
        id: "2",
        marker: {
          symbol: "circle",
        },
        data: getData(StatusId.PortfolioMA),
      },
      {
        type: "scatter",
        name: "Prospect M&A",
        id: "2",
        marker: {
          symbol: "circle",
        },
        data: getData(StatusId.ProspectMA),
      },
    ];

    this.options = {
      credits: {
        enabled: false,
      },
      chart: {
        type: "scatter",
        zooming: {
          type: "x",
        },
        marginBottom: 150,
        events: {
          load: function () {
            const chart = this;
            const buttonY = chart.chartHeight - 100;
            const buttonX = chart.chartWidth - chart.chartWidth * 0.085;

            // State to track if the button has been clicked
            let isClicked = false;

            const isIpad = () => {
              return /iPad|Macintosh/.test(navigator.userAgent) && "ontouchend" in document;
            };

            const buttonConfig = {
              zIndex: 3,
              fill: "#FF6C39", // Initial fill
              stroke: "#FF6C39",
              r: 5,
              style: {
                color: "#FFFFFF", // Initial text color
                fontFamily: "Popins, sans-serif",
              },
            };

            const hoverConfig = isIpad()
              ? {}
              : {
                  fill: "#FFFFFF", // Hover fill for non-iPad
                  stroke: "#FF6C39",
                  r: 5,
                  style: {
                    color: "#FF6C39", // Hover text color for non-iPad
                    fontFamily: "Popins, sans-serif",
                  },
                };

            const toggleButton = chart.renderer
              .button(
                "Toggle Stages",
                buttonX,
                buttonY,
                function () {
                  let allSeriesVisible = chart.series.every((series) => series.visible);

                  chart.series.forEach(function (series) {
                    series.setVisible(!allSeriesVisible, false);
                  });

                  chart.redraw();
                },
                buttonConfig,
                hoverConfig
              )
              .add();

            Highcharts.addEvent(chart, "redraw", function () {
              const newX = chart.chartWidth - chart.chartWidth * 0.085;
              toggleButton.attr({
                x: newX,
              });
            });
          },
        },
        resetZoomButton: {
          position: {
            align: "left",
            x: 10,
            y: 2,
          },
          theme: {
            zIndex: 3,
            fill: "#FF6C39",
            stroke: "#FF6C39",
            r: 5,
            style: {
              color: "#FFFFFF",
              fontFamily: "Popins, sans-serif",
            },
            states: {
              hover: {
                fill: "#FFFFFF",
                stroke: "#FF6C39",
                style: {
                  color: "#FF6C39",
                  fontFamily: "Popins, sans-serif",
                },
              },
            },
          },
        },
      },
      title: {
        text: "Company Interaction Score Over Time",
        align: "left",
      },
      xAxis: {
        type: "datetime",
        title: {
          text: "Recency",
        },
        labels: {
          format: "{value:%e %b %Y}",
        },
        startOnTick: true,
        endOnTick: true,
        showLastLabel: true,
      },
      yAxis: {
        title: {
          text: "Intensity",
        },
        labels: {
          format: "{value}",
        },
        min: 0,
        max: 170,
      },
      legend: {
        enabled: true,
      },
      plotOptions: {
        scatter: {
          marker: {
            radius: isIpad() ? 5 : 2.5,
            symbol: "circle",
            states: {
              hover: {
                enabled: true,
                lineColor: "rgb(100,100,100)",
              },
            },
          },
          states: {
            hover: {
              enabled: false,
            },
          },
          jitter: {
            x: 0.005,
          },
          point: {
            events: {
              click: this.pointClick.bind(this),
            },
          },
        },
      },
      tooltip: {
        pointFormat: "Name: {point.name} <br/> Date: {point.x:%e %b %Y} <br/> Score: {point.y}",
      },
      series,
    };
  }

  pointClick(event: any) {
    const pointData = event.point;
    if (pointData.options) {
      const name = pointData.options.name;

      this.selectedCompany = this.companyData.find((e) => e.name == name);
    }
    this.visible = true;
  }

  toggleVisible(visible: boolean) {
    this.visible = visible;
  }

  generateInteractionData() {
    this.mapLoading = true;

    const dcCompanyTypes = typeof(this.interactionFilter.dcCompanyTypes) === 'number' ? [this.interactionFilter.dcCompanyTypes] : this.interactionFilter.dcCompanyTypes;
    console.log('in dcCompanyTypes', dcCompanyTypes,  typeof(this.interactionFilter.dcCompanyTypes));
    const interactionInitiatorsInclude = this.interactionFilter.interactionInitiatorsInclude
      ? this.interactionFilter.interactionInitiatorsInclude
      : undefined;

    this.companiesService
      .getInteractionChart(this.interactionFilter.interactionTypes, 90, interactionInitiatorsInclude, dcCompanyTypes)
      .pipe(
        takeUntil(this.routerService.destroy$),
        catchError((error) => {
          this.messageService.add({ severity: "error", summary: "Error getting chart data, try again." });
          return of(null);
        })
      )
      .subscribe((response) => {
        if (response?.data) {
          this.routerService.updateUrlQueryParams(this.interactionFilter, InvestorTab.PIPELINE);
          this.companyData = response.data;
          this.initializeChart();
          this.mapLoading = false;
        }
      });
  }
}
